import { apiFetch } from './client';

// AuditEntry mirrors the API shape returned by GET /organizations/{slug}/audit.
// One row per mutation in the system, regardless of actor (user / AI / worker
// / system). The `metadata` field varies by `action` — callers narrow it
// based on the action constant.

export type AuditActorType = 'user' | 'ai' | 'worker' | 'system';

export interface AuditEntry {
  id: string;
  actor_user_id?: string;
  // Joined from core.user. Empty for worker/system rows or when the user
  // was hard-deleted; the SPA falls back to actor_type i18n in that case.
  actor_name?: string;
  actor_email?: string;
  actor_type: AuditActorType;
  action: string; // e.g. 'members_import' — i18n key derives from this
  summary: string;
  metadata?: unknown; // shape depends on action; cast at the call site
  command_intent_id?: string;
  // True when the linked CommandIntent is still inside its 30-day undo
  // window AND has stored inverse_ops. The activity feed uses this to
  // render the revert button on the audit row that REPLACED the executed
  // intent in the merged feed. Stays false for direct SPA service calls.
  command_intent_revertable?: boolean;
  created_at: string;
}

export interface AuditCursor {
  created_at: string;
  id: string;
}

export interface ListAuditResponse {
  items: AuditEntry[];
  next_cursor?: AuditCursor | null;
  total: number;
}

interface ListOpts {
  cursor?: AuditCursor;
  limit?: number;
  // Optional filters
  actorUserIds?: string[];
  actions?: string[];
  createdAfter?: string;  // RFC3339
  createdBefore?: string; // RFC3339
  // Narrow to entries ABOUT this member (per-member Logs tab).
  subjectMemberId?: string;
}

export async function listAudit(
  token: string | null,
  slug: string,
  opts: ListOpts = {},
): Promise<ListAuditResponse> {
  const qs = new URLSearchParams();
  if (opts.cursor) {
    qs.set('before_created_at', opts.cursor.created_at);
    qs.set('before_id', opts.cursor.id);
  }
  if (opts.limit) qs.set('limit', String(opts.limit));
  for (const id of opts.actorUserIds ?? []) qs.append('actor_user_id', id);
  for (const a of opts.actions ?? []) qs.append('action', a);
  if (opts.createdAfter) qs.set('created_after', opts.createdAfter);
  if (opts.createdBefore) qs.set('created_before', opts.createdBefore);
  if (opts.subjectMemberId) qs.set('subject_member_id', opts.subjectMemberId);
  const query = qs.toString();
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/audit${query ? `?${query}` : ''}`;
  return apiFetch<ListAuditResponse>(path, { token });
}

// Metadata shape per action — keep in lockstep with the server-side payload
// in internal/api/members.go (bulkImport). Adding a new action: add the
// shape here too so the Activity page can render specific stats.
export interface MembersImportMetadata {
  imported: number;
  merged: number;
  teams_created: number;
  skipped_count: number;
}
